import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { StoreType } from 'reducers/types';
import { buildIdentifyPayload } from 'utils/helpscoutIdentity';

const HELPSCOUT_SCRIPT_ID = 'helpscout-beacon-script';
const HELPSCOUT_SCRIPT_SRC = 'https://beacon-v2.helpscout.net';
const DEFAULT_BEACON_ID = 'bb1316c1-20ff-4286-b189-a2c159f0fa4a';
const BEACON_CONFIG = {
  messaging: {
    contactForm: {
      showSubject: true,
    },
  },
};

const HELPSCOUT_LOADING_KEY = '__helpscoutLoading';
const HELPSCOUT_INITIALIZED_KEY = '__helpscoutInitialized';
const HELPSCOUT_INVOKE_KEY = '__helpscoutInvoke';
const BEACON_CONTAINER_ID = 'beacon-container';
const BEACON_CONTAINER_DISPLAY_ATTR = 'data-helpscout-original-display';
const HIDE_ROUTE_PATTERN = /^\/programs\/[^/]+\/sessions\/[^/]+\/(start|ponder)$/;

type VisibilityState = 'hidden' | 'shown';

type BeaconMethodArgs = [string, ...unknown[]];

type BeaconQueueEntry = {
  method: string;
  options?: unknown;
  data?: unknown;
};

type BeaconFunction = {
  (method: string, ...args: unknown[]): void;
  readyQueue?: BeaconQueueEntry[];
};

type HelpscoutContextValue = {
  ready: boolean;
  invoke: (...args: BeaconMethodArgs) => void;
};

const noop: HelpscoutContextValue['invoke'] = () => {};

const HelpscoutContext = createContext<HelpscoutContextValue>({
  ready: false,
  invoke: noop,
});

const debug = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[HelpscoutProvider]', ...args);
};

type HelpscoutProviderProps = {
  children: ReactNode;
  beaconId?: string;
};

type BeaconWindow = Window &
  typeof window & {
    Beacon?: BeaconFunction;
    __helpscoutLoading?: boolean;
    __helpscoutInitialized?: boolean;
    __helpscoutInvoke?: HelpscoutContextValue['invoke'];
  };

const getWindow = () => (typeof window === 'undefined' ? undefined : (window as BeaconWindow));

const ensureBeaconStub = (currentWindow: BeaconWindow) => {
  if (typeof currentWindow.Beacon === 'function') {
    return;
  }

  const beaconStub: BeaconFunction = ((method: string, options?: unknown, data?: unknown) => {
    if (!beaconStub.readyQueue) {
      beaconStub.readyQueue = [];
    }

    beaconStub.readyQueue.push({
      method,
      options,
      data,
    });
  }) as BeaconFunction;

  beaconStub.readyQueue = [];

  currentWindow.Beacon = beaconStub;
  debug('Installed Beacon stub');
};

const loadBeaconScript = (
  currentWindow: BeaconWindow,
  onLoad?: () => void,
  onError?: (event: Event) => void,
) => {
  if (currentWindow[HELPSCOUT_LOADING_KEY]) {
    return;
  }

  const { document } = currentWindow;
  if (!document || document.getElementById(HELPSCOUT_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = HELPSCOUT_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.src = HELPSCOUT_SCRIPT_SRC;
  script.addEventListener('load', () => {
    debug('Beacon loader script loaded');
    onLoad?.();
  });
  script.addEventListener('error', (event) => {
    debug('Beacon loader script failed', event);
    onError?.(event);
  });

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
  const windowWithLoaderFlag = currentWindow;
  windowWithLoaderFlag[HELPSCOUT_LOADING_KEY] = true;
  debug('Beacon loader script appended to html');
};

const hideContainer = (container: HTMLElement) => {
  if (!container.hasAttribute(BEACON_CONTAINER_DISPLAY_ATTR)) {
    container.setAttribute(BEACON_CONTAINER_DISPLAY_ATTR, container.style.display || '');
  }
  container.style.setProperty('display', 'none', 'important');
};

const showContainer = (container: HTMLElement) => {
  const beaconContainer = container;
  const prev = container.getAttribute(BEACON_CONTAINER_DISPLAY_ATTR);
  if (prev === null) {
    beaconContainer.style.removeProperty('display');
    return;
  }

  if (prev) {
    beaconContainer.style.display = prev;
  } else {
    beaconContainer.style.removeProperty('display');
  }

  beaconContainer.removeAttribute(BEACON_CONTAINER_DISPLAY_ATTR);
};

export const HelpscoutProvider = ({
  children,
  beaconId = DEFAULT_BEACON_ID,
}: HelpscoutProviderProps) => {
  const router = useRouter();
  const user = useSelector((state: StoreType) => state.user);
  const [ready, setReady] = useState(false);
  const desiredVisibilityRef = useRef<VisibilityState>('shown');
  const visibilityPollerRef = useRef<number | null>(null);
  const initializedRef = useRef(false);
  const readyHandlerRef = useRef<(() => void) | null>(null);
  const lastIdentifyPayloadRef = useRef<string | null>(null);

  const applyVisibilityNow = useCallback(() => {
    const currentWindow = getWindow();
    const container = currentWindow?.document?.getElementById(BEACON_CONTAINER_ID);

    if (!container) {
      debug('Beacon container not ready; visibility update deferred', {
        target: desiredVisibilityRef.current,
      });
      return false;
    }

    if (desiredVisibilityRef.current === 'hidden') {
      hideContainer(container);
    } else {
      showContainer(container);
    }

    return true;
  }, []);

  const clearVisibilityPoller = useCallback(() => {
    if (visibilityPollerRef.current === null) {
      return;
    }

    const currentWindow = getWindow();
    if (!currentWindow) {
      visibilityPollerRef.current = null;
      return;
    }

    currentWindow.clearInterval(visibilityPollerRef.current);
    visibilityPollerRef.current = null;
  }, []);

  const scheduleVisibilitySync = useCallback(() => {
    if (applyVisibilityNow()) {
      clearVisibilityPoller();
      return;
    }

    const currentWindow = getWindow();
    if (!currentWindow || visibilityPollerRef.current !== null) {
      return;
    }

    visibilityPollerRef.current = currentWindow.setInterval(() => {
      if (applyVisibilityNow()) {
        clearVisibilityPoller();
      }
    }, 250);
  }, [applyVisibilityNow, clearVisibilityPoller]);

  const invoke = useCallback<HelpscoutContextValue['invoke']>((method, ...args) => {
    const currentWindow = getWindow();
    if (!currentWindow) {
      debug('Beacon invoke skipped; window unavailable', { method });
      return;
    }

    ensureBeaconStub(currentWindow);
    const beacon = currentWindow.Beacon;

    if (typeof beacon !== 'function') {
      debug('Beacon invoke skipped; Beacon missing', { method });
      return;
    }

    try {
      beacon(method, ...args);
    } catch (error) {
      debug('Beacon invoke failed', { method, error });
    }
  }, []);

  const handleReady = useCallback(() => {
    debug('Beacon ready');
    setReady(true);
    scheduleVisibilitySync();
  }, [scheduleVisibilitySync]);

  const initializeBeacon = useCallback(
    (currentWindow: BeaconWindow) => {
      ensureBeaconStub(currentWindow);
      const beacon = currentWindow.Beacon;

      if (typeof beacon !== 'function') {
        debug('Beacon initialization deferred; Beacon missing');
        return;
      }

      if (!initializedRef.current) {
        beacon('init', beaconId);
        beacon('config', BEACON_CONFIG);
        const windowWithInitFlag = currentWindow;
        windowWithInitFlag[HELPSCOUT_INITIALIZED_KEY] = true;
        initializedRef.current = true;
        debug('Beacon init/config dispatched', { beaconId });
      }

      if (!readyHandlerRef.current) {
        readyHandlerRef.current = handleReady;
      }

      const readyHandler = readyHandlerRef.current;

      if (readyHandler) {
        try {
          beacon('off', 'ready', readyHandler);
        } catch (error) {
          debug('Beacon ready handler detach failed', error);
        }

        beacon('on', 'ready', readyHandler);
      }

      scheduleVisibilitySync();
    },
    [beaconId, handleReady, scheduleVisibilitySync],
  );

  const setVisibility = useCallback(
    (next: VisibilityState) => {
      if (desiredVisibilityRef.current === next) {
        scheduleVisibilitySync();
        return;
      }

      desiredVisibilityRef.current = next;
      debug('Requested Beacon visibility change', { next });

      if (next === 'hidden') {
        invoke('close');
      }

      scheduleVisibilitySync();
    },
    [invoke, scheduleVisibilitySync],
  );

  const evaluateRoute = useCallback(
    (url?: string) => {
      if (!url) {
        return;
      }

      const path = url.split('#')[0]?.split('?')[0] || '/';
      const shouldHide = HIDE_ROUTE_PATTERN.test(path);
      debug('Route evaluated for Helpscout visibility', { path, shouldHide });
      setVisibility(shouldHide ? 'hidden' : 'shown');
    },
    [setVisibility],
  );

  useEffect(() => {
    const currentWindow = getWindow();
    if (!currentWindow) {
      return undefined;
    }

    initializeBeacon(currentWindow);
    loadBeaconScript(currentWindow, () => initializeBeacon(currentWindow));

    return () => {
      clearVisibilityPoller();

      const container = currentWindow.document?.getElementById(BEACON_CONTAINER_ID);
      if (container) {
        showContainer(container);
      }

      if (readyHandlerRef.current && typeof currentWindow.Beacon === 'function') {
        try {
          currentWindow.Beacon('off', 'ready', readyHandlerRef.current);
        } catch (error) {
          debug('Beacon ready handler detach failed during cleanup', error);
        }
      }
      readyHandlerRef.current = null;

      if (currentWindow[HELPSCOUT_INVOKE_KEY] === invoke) {
        delete currentWindow[HELPSCOUT_INVOKE_KEY];
      }

      if (currentWindow[HELPSCOUT_INITIALIZED_KEY]) {
        delete currentWindow[HELPSCOUT_INITIALIZED_KEY];
      }

      initializedRef.current = false;
      readyHandlerRef.current = null;
    };
  }, [clearVisibilityPoller, initializeBeacon, invoke]);

  useEffect(() => {
    if (!router?.isReady) {
      return undefined;
    }

    evaluateRoute(router.asPath);

    const handleRouteChange = (nextUrl: string) => evaluateRoute(nextUrl);

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [evaluateRoute, router]);

  const contextValue = useMemo<HelpscoutContextValue>(
    () => ({
      ready,
      invoke,
    }),
    [invoke, ready],
  );

  useEffect(() => {
    const currentWindow = getWindow();
    if (!currentWindow) {
      return undefined;
    }

    currentWindow[HELPSCOUT_INVOKE_KEY] = invoke;
    debug('Beacon invoke stored on window');

    return () => {
      if (currentWindow[HELPSCOUT_INVOKE_KEY] === invoke) {
        delete currentWindow[HELPSCOUT_INVOKE_KEY];
        debug('Beacon invoke removed from window');
      }
    };
  }, [invoke]);

  useEffect(() => {
    if (user?.email) {
      return;
    }

    lastIdentifyPayloadRef.current = null;
    debug('Identify payload cache cleared');
  }, [user?.email]);

  useEffect(() => {
    if (!ready || !user?.email) {
      debug('Identify skipped; waiting for readiness or user email', {
        ready,
        hasEmail: Boolean(user?.email),
      });
      return;
    }

    const payload = buildIdentifyPayload({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    });

    if (!payload) {
      debug('Identify skipped; payload missing');
      return;
    }

    const serializedPayload = JSON.stringify(payload);
    if (lastIdentifyPayloadRef.current === serializedPayload) {
      debug('Identify skipped; payload unchanged');
      return;
    }

    lastIdentifyPayloadRef.current = serializedPayload;
    const loggablePayload = { ...payload };
    delete loggablePayload.signature;

    debug('Beacon identify invoked', { payload: loggablePayload });
    invoke('identify', payload);
  }, [invoke, ready, user?.email, user?.firstName, user?.lastName]);

  return <HelpscoutContext.Provider value={contextValue}>{children}</HelpscoutContext.Provider>;
};

type UseHelpscoutResult = {
  ready: boolean;
  open: () => void;
  close: () => void;
  identify: (payload: Record<string, unknown>) => void;
  config: (config: Record<string, unknown>) => void;
  invoke: HelpscoutContextValue['invoke'];
};

export const useHelpscout = (): UseHelpscoutResult => {
  const { ready, invoke } = useContext(HelpscoutContext);

  return useMemo(
    () => ({
      ready,
      open: () => invoke('open'),
      close: () => invoke('close'),
      identify: (payload: Record<string, unknown>) => invoke('identify', payload),
      config: (config: Record<string, unknown>) => invoke('config', config),
      invoke,
    }),
    [invoke, ready],
  );
};
